import type { FormDoc } from "@koaform/form-schema";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

/**
 * Local breadcrumbs so the landing page can offer "Resume *Customer survey*".
 *
 * Deliberately NOT the anonymous session credential. That lives in an httpOnly
 * cookie and is never handed to JavaScript, so any XSS on this origin cannot
 * walk off with a session. What is stored here is the per-draft resume token,
 * which the user is shown anyway, is scoped to one draft, and is the
 * documented recovery path when the cookie is cleared.
 */
const DRAFTS_KEY = "koaform.drafts";

export type DraftBreadcrumb = {
  id: string;
  title: string;
  updatedAt: string;
  resumeToken?: string;
};

export const readBreadcrumbs = (): DraftBreadcrumb[] => {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    return raw ? (JSON.parse(raw) as DraftBreadcrumb[]) : [];
  } catch {
    return [];
  }
};

export const rememberDraft = (crumb: DraftBreadcrumb) => {
  try {
    const all = readBreadcrumbs();
    const existing = all.find((c) => c.id === crumb.id);
    // The resume token is only ever returned once, at creation. Later saves
    // must not clobber it, or the recovery path silently disappears.
    const merged: DraftBreadcrumb = {
      ...crumb,
      resumeToken: crumb.resumeToken ?? existing?.resumeToken,
    };
    const others = all.filter((c) => c.id !== crumb.id);
    localStorage.setItem(
      DRAFTS_KEY,
      JSON.stringify([merged, ...others].slice(0, 10)),
    );
  } catch {
    // Private mode with storage disabled — the cookie alone still works.
  }
};

/**
 * The work-in-progress mirror: the current document, written on every edit.
 *
 * The server draft is the source of truth, but two windows would lose work on
 * a refresh without this: the ~1.5s autosave debounce, and everything typed
 * before the first save creates the draft. Non-secret document content only —
 * tokens never go in localStorage.
 */
const WIP_KEY = "koaform.wip";

export type Wip = { draftId: string | null; doc: FormDoc; at: number };

export const writeWip = (wip: Wip) => {
  try {
    localStorage.setItem(WIP_KEY, JSON.stringify(wip));
  } catch {
    // Private mode with storage disabled — autosave alone still works.
  }
};

export const readWip = (): Wip | null => {
  try {
    const raw = localStorage.getItem(WIP_KEY);
    if (!raw) return null;
    const wip = JSON.parse(raw) as Wip;
    // A stale mirror should not hijack next week's brand-new form.
    if (Date.now() - wip.at > 24 * 60 * 60 * 1000) return null;
    return wip;
  } catch {
    return null;
  }
};

export const clearWip = () => {
  try {
    localStorage.removeItem(WIP_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
};

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown,
    public readonly fieldErrors?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** True when a save lost the optimistic-concurrency race against another tab. */
export const isRevisionConflict = (err: unknown): err is ApiError =>
  err instanceof ApiError && err.code === "revision_conflict";

const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("content-type", "application/json");

  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers,
    credentials: "include",
  });

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiError(
      res.status,
      payload?.error?.message ?? "Something went wrong.",
      payload?.error?.code,
      payload?.error?.details,
      payload?.fieldErrors,
    );
  }
  return payload?.data as T;
};

export type Me = { id: string; email: string; name?: string };

export type Draft = {
  id: string;
  title: string;
  /** The owner's private label. Null = fall back to the title. */
  name: string | null;
  /** Draft differs from the live version — edits are saved but not public. */
  hasUnpublishedChanges: boolean;
  status: "draft" | "published" | "unpublished";
  /** Null until first publish; never changes after. */
  slug: string | null;
  responsesCount: number;
  schema: FormDoc;
  revision: number;
  updatedAt: string;
  expiresAt: string | null;
  /** Returned once, at creation. Never re-fetchable — show it, then store it. */
  resumeToken?: string;
  requiresConfirmation?: boolean;
};

export type CsvColumn = {
  fieldId: string;
  label: string;
  type: string;
  removed: boolean;
};

export type ResponseRow = {
  id: string;
  submittedAt: string;
  cells: Record<string, string>;
};

export type AdminUser = {
  user: {
    id: string;
    email: string;
    name: string | null;
    signedUpWith: string;
    emailVerified: boolean;
    isBanned: boolean;
    createdAt: string;
    lastLoginAt: string | null;
    plan: Record<string, unknown>;
    limits: Record<string, number>;
  };
  effectiveLimits: Record<string, number>;
  defaults: Record<string, number>;
  forms: {
    id: string;
    title: string;
    name: string | null;
    slug: string | null;
    status: string;
    responsesCount: number;
    createdAt: string;
    updatedAt: string;
  }[];
  totals: { forms: number; published: number; responses: number };
};

export type AdminOverview = {
  users: { users: number; last24h: number; last7d: number };
  forms: { forms: number; published: number };
  responses: { responses: number; last24h: number };
  recentSignups: { email: string; createdAt: string }[];
};

export const api = {
  adminOverview: () => request<AdminOverview>("/admin/overview"),

  adminFindUser: (email: string) =>
    request<AdminUser>(`/admin/users?email=${encodeURIComponent(email)}`),

  adminUpdateUser: (
    id: string,
    body: {
      /** null on a limit removes the override. */
      limits?: Record<string, number | null>;
      plan?: Record<string, unknown>;
      isBanned?: boolean;
    },
  ) =>
    request<unknown>(`/admin/users/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  listDrafts: () => request<Draft[]>("/drafts"),

  getDraft: (id: string) => request<Draft>(`/drafts/${id}`),

  createDraft: (schema: FormDoc) =>
    request<Draft>("/drafts", {
      method: "POST",
      body: JSON.stringify({ schema }),
    }),

  saveDraft: (id: string, schema: FormDoc, revision: number) =>
    request<Draft>(`/drafts/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ schema, revision }),
    }),

  resumeDraft: (token: string) =>
    request<Draft>(`/drafts/resume/${encodeURIComponent(token)}`),

  deleteDraft: (id: string) =>
    request<{ id: string }>(`/drafts/${id}`, { method: "DELETE" }),

  duplicateDraft: (id: string) =>
    request<Draft>(`/drafts/${id}/duplicate`, { method: "POST" }),

  /** Throws away edits made since the last publish. */
  revertDraft: (id: string) =>
    request<Draft>(`/drafts/${id}/revert`, { method: "POST" }),

  /**
   * The private label, not the public title. Carries no revision because the
   * name is not document content — so it cannot lose a race with an editor
   * tab, and it never changes what respondents see. "" clears it.
   */
  renameDraft: (id: string, name: string) =>
    request<Draft>(`/drafts/${id}/name`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  publish: (formId: string) =>
    request<{ id: string; slug: string; status: string }>(
      `/forms/${formId}/publish`,
      { method: "POST" },
    ),

  formSummary: (formId: string) =>
    request<{
      id: string;
      title: string;
      slug: string | null;
      responses: number;
    }>(`/forms/${formId}/summary`),

  listResponses: (formId: string, cursor?: string) =>
    request<{
      columns: CsvColumn[];
      responses: ResponseRow[];
      nextCursor: string | null;
    }>(
      `/forms/${formId}/responses${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
    ),

  getResponse: (formId: string, responseId: string) =>
    request<{
      id: string;
      submittedAt: string;
      answers: { fieldId: string; label: string; value: string }[];
    }>(`/forms/${formId}/responses/${responseId}`),

  deleteResponse: (formId: string, responseId: string) =>
    request<{ id: string }>(`/forms/${formId}/responses/${responseId}`, {
      method: "DELETE",
    }),

  /** Cropped image bytes → the app-relative URL the theme stores. */
  uploadThemeImage: async (
    formId: string,
    slot: "logo" | "banner",
    image: Blob,
  ) => {
    const res = await fetch(
      `${API_URL}/drafts/${formId}/theme-image?slot=${slot}`,
      {
        method: "POST",
        credentials: "include",
        headers: { "content-type": image.type || "image/png" },
        body: image,
      },
    );
    const payload = await res.json().catch(() => null);
    if (!res.ok) {
      throw new ApiError(
        res.status,
        payload?.error?.message ?? "Could not upload that image.",
        payload?.error?.code,
      );
    }
    return payload?.data as { url: string };
  },

  me: () => request<Me>("/auth/me"),

  updateMe: (name: string) =>
    request<Me>("/auth/me", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),

  logout: () => request<{ ok: boolean }>("/auth/logout", { method: "POST" }),

  /** Turnstile guards this one; the token rides in a header, as the API expects. */
  requestCode: (email: string, captchaToken?: string) =>
    request<{ sent: boolean }>("/auth/email", {
      method: "POST",
      body: JSON.stringify({ email }),
      headers: captchaToken ? { "cf-turnstile-token": captchaToken } : {},
    }),

  /** Signing in also claims any anonymous drafts this browser is holding. */
  verifyCode: (email: string, code: string) =>
    request<{ user: { id: string; email: string }; claimedForms: string[] }>(
      "/auth/email/verify",
      { method: "POST", body: JSON.stringify({ email, code }) },
    ),
};
